import Cookies from "js-cookie";
import {
	fetchImageUrlAsFile,
	FAVICON_FETCH_STRATEGY,
	getFaviconImageUrl,
	isFirebaseStorageImageUrl
} from './favicon';
import {SUPPORTED_LINK_IMAGE_FORMATS} from './imageUpload';
import {isSafeLinkUrl} from './linkUrl';
import {
	addCategory,
	BUNDLED_DEFAULT_LINK_IMAGE,
	deleteCategory,
	deleteProfile,
	fetchCategories,
	fetchLinks,
	fetchProfiles,
	updateCategory,
	updateLink,
	uploadNewImage
} from "./firebaseTools";
import {
	getProfileIdsFromEntity,
	hasAllProfilesScope,
	includesProfile,
	normalizeProfileId,
	normalizeProfileIds,
	persistProfileIdsForSave
} from './profileSelection';

const sortLinks = (links) => {
	return links.sort((a, b) => a.name.localeCompare(b.name));
};

export {isSafeLinkUrl} from './linkUrl';

export const MAX_CATEGORIES = 5;
export const APP_LOGIN_BADGE_URL = `${process.env.PUBLIC_URL || ''}/linkspace-login-badge.png`;
export const LEGACY_ALL_PROFILE_ID = "__ALL__";
export const VIRTUAL_ALL_PROFILE_ID = "all_profiles";

export {
	expandProfileIdsForSelection,
	getProfileIdsFromEntity,
	hasAllProfilesScope,
	includesProfile,
	normalizeProfileId,
	normalizeProfileIds,
	persistProfileIdsForSave,
	removeProfileFromScope
} from './profileSelection';

export const ALL_PROFILE = {id: VIRTUAL_ALL_PROFILE_ID, profileName: "ALL", isVirtual: true};

export const toFiniteNumber = (value, fallback = 0) => {
	const numeric = Number(value);
	return Number.isFinite(numeric) ? numeric : fallback;
};

export const normalizeProfile = (profile) => {
	if (!profile) return null;
	return {
		id: normalizeProfileId(profile.id),
		profileName: String(profile.profileName ?? "").toUpperCase(),
		isVirtual: Boolean(profile.isVirtual),
	};
};

export const formatProfileName = (name) => String(name ?? '').trim().toUpperCase();

export const normalizeProfileNameForComparison = (name) => formatProfileName(name);

const migrateProfileIdsOnMerge = (profileIds, sourceProfileId, targetProfileId) => {
	const nextProfileIds = normalizeLink({profileIds}).profileIds
		.filter((id) => id !== sourceProfileId);
	if (!nextProfileIds.includes(targetProfileId)) {
		nextProfileIds.push(targetProfileId);
	}
	return nextProfileIds;
};

export const findProfileByName = (profiles, name) => {
	const normalized = normalizeProfileNameForComparison(name);
	if (!normalized) return null;
	return (profiles ?? []).find(
		(item) => normalizeProfileNameForComparison(item.profileName) === normalized
	) ?? null;
};

export const findProfileRenameCollisionTarget = (profiles, sourceProfileId, nextProfileName) => {
	const normalizedNextName = normalizeProfileNameForComparison(nextProfileName);
	if (!normalizedNextName || !sourceProfileId) {
		return null;
	}

	return (profiles ?? []).find((item) =>
		item?.id
		&& item.id !== sourceProfileId
		&& item.id !== VIRTUAL_ALL_PROFILE_ID
		&& normalizeProfileNameForComparison(item.profileName) === normalizedNextName
	) ?? null;
};

export const mergeProfileOnRenameCollision = async ({
	sourceProfileId,
	targetProfileId,
	allCategories,
	allLinks,
	addError
}) => {
	let nextLinksState = [...allLinks];
	for (const link of allLinks) {
		if (!includesProfile(link, sourceProfileId)) {
			continue;
		}

		const nextProfileIds = migrateProfileIdsOnMerge(link.profileIds, sourceProfileId, targetProfileId);
		const baseName = getFormattedValue(String(link.name ?? '').replace(/ \(\d+\)$/, ''));
		const nextName = generateUniqueLinkName(
			nextLinksState.filter((item) => item.id !== link.id),
			baseName,
			link.category,
			true,
			true,
			targetProfileId
		);
		const migratedLink = {
			...normalizeLink(link),
			profileIds: nextProfileIds,
			name: nextName
		};
		await updateLink(link.id, {profileIds: nextProfileIds, name: nextName}, addError);
		nextLinksState = nextLinksState.map((item) => item.id === link.id ? migratedLink : item);
	}

	let nextCategoriesState = (allCategories ?? []).map((category) => ({
		...category,
		idxPerProfile: {...(category.idxPerProfile ?? {})}
	}));
	for (const category of nextCategoriesState) {
		if (!includesProfile(category, sourceProfileId)) {
			continue;
		}

		const nextProfileIds = migrateProfileIdsOnMerge(category.profileIds, sourceProfileId, targetProfileId)
			.filter((id) => id && id !== VIRTUAL_ALL_PROFILE_ID);
		const nextIdxPerProfile = {...category.idxPerProfile};
		const sourceIdx = nextIdxPerProfile[sourceProfileId];
		if (sourceIdx != null) {
			if (nextIdxPerProfile[targetProfileId] == null) {
				nextIdxPerProfile[targetProfileId] = sourceIdx;
			} else {
				nextIdxPerProfile[targetProfileId] = Math.min(
					Number(nextIdxPerProfile[targetProfileId]),
					Number(sourceIdx)
				);
			}
			delete nextIdxPerProfile[sourceProfileId];
		} else if (nextIdxPerProfile[targetProfileId] == null) {
			nextIdxPerProfile[targetProfileId] = calculateNextIdxForProfile(targetProfileId, nextCategoriesState);
		}

		const updatedCategory = {
			...category,
			profileIds: nextProfileIds,
			idxPerProfile: nextIdxPerProfile
		};
		await updateCategory(updatedCategory, addError);
		nextCategoriesState = nextCategoriesState.map((item) =>
			item.id === updatedCategory.id ? updatedCategory : item
		);
	}

	nextCategoriesState = await reindexCategoriesForProfiles(
		nextCategoriesState,
		[targetProfileId],
		addError
	);
	await deleteProfile(sourceProfileId, addError);

	return {
		categories: nextCategoriesState,
		links: nextLinksState
	};
};

export const normalizeLink = (link) => {
	let profileIds = [];
	if (Array.isArray(link.profileIds)) {
		profileIds = link.profileIds;
	} else if (link.profileId) {
		profileIds = [link.profileId];
	}

	return {
		...link,
		profileIds: normalizeProfileIds(profileIds),
	};
};

export const normalizeCategory = (category, fallbackProfileId = null) => {
	let rawProfileIds = [];
	if (Array.isArray(category.profileIds)) {
		rawProfileIds = category.profileIds;
	} else if (category.profileId) {
		rawProfileIds = [category.profileId];
	}
	const profileIds = normalizeProfileIds(rawProfileIds);
	const idxPerProfile = {};

	if (category.idxPerProfile && typeof category.idxPerProfile === "object") {
		Object.entries(category.idxPerProfile).forEach(([profileId, idx]) => {
			const normalizedProfileId = normalizeProfileId(profileId);
			if (!normalizedProfileId) return;
			idxPerProfile[normalizedProfileId] = toFiniteNumber(idx);
		});
	}

	const normalizedFallbackProfileId = normalizeProfileId(fallbackProfileId);
	if (category.idx != null && normalizedFallbackProfileId && idxPerProfile[normalizedFallbackProfileId] == null) {
		idxPerProfile[normalizedFallbackProfileId] = toFiniteNumber(category.idx);
	}

	return {
		...category,
		profileIds,
		idxPerProfile,
	};
};

export const isAllProfile = (profile) => normalizeProfileId(profile?.id) === VIRTUAL_ALL_PROFILE_ID;

export const getCategoryIdxForProfile = (category, profileId) => {
	if (!category) return 0;
	const normalizedProfileId = normalizeProfileId(profileId);
	if (isAllProfile({id: normalizedProfileId})) {
		if (category.idx != null) {
			return toFiniteNumber(category.idx);
		}
		if (category.idxPerProfile?.[VIRTUAL_ALL_PROFILE_ID] != null) {
			return toFiniteNumber(category.idxPerProfile[VIRTUAL_ALL_PROFILE_ID]);
		}
		if (category.idxPerProfile?.[LEGACY_ALL_PROFILE_ID] != null) {
			return toFiniteNumber(category.idxPerProfile[LEGACY_ALL_PROFILE_ID]);
		}
		const idxValues = Object.values(category.idxPerProfile ?? {}).map((idx) => toFiniteNumber(idx)).filter(Number.isFinite);
		return idxValues.length ? Math.min(...idxValues) : toFiniteNumber(category.idx);
	}

	if (category.idxPerProfile?.[normalizedProfileId] != null) {
		return toFiniteNumber(category.idxPerProfile[normalizedProfileId]);
	}
	if (profileId === LEGACY_ALL_PROFILE_ID && category.idxPerProfile?.[LEGACY_ALL_PROFILE_ID] != null) {
		return toFiniteNumber(category.idxPerProfile[LEGACY_ALL_PROFILE_ID]);
	}
	return toFiniteNumber(category.idx);
};

export const sortProfiles = (profiles) => {
	const virtualAll = profiles.find((profile) => profile.id === VIRTUAL_ALL_PROFILE_ID);
	const rest = profiles
		.filter((profile) => profile.id !== VIRTUAL_ALL_PROFILE_ID)
		.filter((profile) => profile?.profileName)
		.sort((a, b) => a.profileName.localeCompare(b.profileName));

	return virtualAll ? [virtualAll, ...rest] : rest;
};

export const calculateNextIdxForProfile = (profileId, categories) => {
	const normalizedProfileId = normalizeProfileId(profileId);
	if (!normalizedProfileId) return 0;
	const values = (categories ?? [])
		.map((category) => getCategoryIdxForProfile(category, normalizedProfileId))
		.filter(Number.isFinite);
	return values.length ? Math.max(...values) + 1 : 0;
};

export const resolveLinkProfileIdsForSave = ({
	link,
	categories,
	profiles,
	selectedProfileIds,
	profileSelectionChanged
}) => {
	if (!profileSelectionChanged) {
		const persistedProfileIds = getProfileIdsFromEntity(link);
		if (persistedProfileIds.length > 0) {
			return persistedProfileIds;
		}

		const category = (categories ?? []).find((item) => item.id === link?.categoryId);
		const categoryProfileIds = getProfileIdsFromEntity(category);
		if (categoryProfileIds.length > 0) {
			return categoryProfileIds;
		}

		return [VIRTUAL_ALL_PROFILE_ID];
	}

	return persistProfileIdsForSave(selectedProfileIds, profiles);
};

export const getFormattedValue = (value) => {
	return value
		.split(' ')
		.map(word => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ');
};

export const normalizeCategoryNameForComparison = (name) =>
	String(name ?? '').trim().toLowerCase();

export const formatCategoryName = (name) =>
	getFormattedValue(String(name ?? '').trim());

const cloneIdxPerProfileMap = (raw) => {
	if (raw != null && typeof raw === 'object') {
		return {...raw};
	}
	return {};
};

const minFiniteIdxFromPerProfile = (nextIdxPerProfile, fallbackIdx) => {
	const idxValues = Object.values(nextIdxPerProfile).map(Number).filter(Number.isFinite);
	return idxValues.length > 0 ? Math.min(...idxValues) : Number(fallbackIdx);
};

export const findCategoryRenameCollisionTarget = (categories, sourceCategoryId, nextCategoryName) => {
	const normalizedNextName = normalizeCategoryNameForComparison(formatCategoryName(nextCategoryName));
	if (!normalizedNextName || !sourceCategoryId) {
		return null;
	}

	return (categories ?? []).find((item) =>
		item?.id
		&& item.id !== sourceCategoryId
		&& normalizeCategoryNameForComparison(item.category) === normalizedNextName
	) ?? null;
};

export const mergeCategoryOnRenameCollision = async ({
	nextCategoryName,
	nextCategoriesState,
	sourceCategory,
	targetCategory,
	allLinks,
	addError
}) => {
	const mergedProfileIds = Array.from(new Set([
		...(targetCategory.profileIds ?? []),
		...(sourceCategory.profileIds ?? [])
	])).filter((id) => id && id !== VIRTUAL_ALL_PROFILE_ID);

	const nextIdxPerProfile = cloneIdxPerProfileMap(targetCategory.idxPerProfile);
	for (const mergedProfileId of mergedProfileIds) {
		const sourceIdx = Number(getCategoryIdxForProfile(sourceCategory, mergedProfileId));
		const targetIdx = Number(getCategoryIdxForProfile(targetCategory, mergedProfileId));
		const hasSource = Number.isFinite(sourceIdx);
		const hasTarget = Number.isFinite(targetIdx);

		if (hasSource && hasTarget) {
			nextIdxPerProfile[mergedProfileId] = Math.min(sourceIdx, targetIdx);
		} else if (hasSource) {
			nextIdxPerProfile[mergedProfileId] = sourceIdx;
		} else if (hasTarget) {
			nextIdxPerProfile[mergedProfileId] = targetIdx;
		}
	}

	const mergedCategory = {
		...targetCategory,
		category: formatCategoryName(nextCategoryName),
		profileIds: mergedProfileIds,
		idxPerProfile: nextIdxPerProfile,
		idx: minFiniteIdxFromPerProfile(nextIdxPerProfile, targetCategory.idx),
	};

	await updateCategory(mergedCategory, addError);
	let mergedCategoriesState = nextCategoriesState
		.map((item) => item.id === mergedCategory.id ? mergedCategory : item)
		.filter((item) => item.id !== sourceCategory.id);

	const linksToMove = allLinks.filter((item) => item.categoryId === sourceCategory.id);
	let nextLinksState = [...allLinks];
	for (const link of linksToMove) {
		const nextName = generateUniqueLinkName(
			nextLinksState.filter((item) => item.id !== link.id),
			getFormattedValue(link.name),
			mergedCategory.category,
			true,
			true,
			link.profileIds?.[0]
		);
		const migratedLink = {
			...link,
			categoryId: mergedCategory.id,
			category: mergedCategory.category,
			name: nextName
		};
		await updateLink(link.id, {
			categoryId: mergedCategory.id,
			category: mergedCategory.category,
			name: nextName
		}, addError);
		nextLinksState = nextLinksState.map((item) => item.id === link.id ? migratedLink : item);
	}

	await deleteCategory(sourceCategory.id, addError);
	const affectedProfiles = Array.from(new Set([
		...(sourceCategory.profileIds ?? []),
		...(mergedCategory.profileIds ?? [])
	])).filter((id) => id && id !== VIRTUAL_ALL_PROFILE_ID);
	mergedCategoriesState = await reindexCategoriesForProfiles(mergedCategoriesState, affectedProfiles, addError);

	return {
		categories: mergedCategoriesState,
		links: nextLinksState
	};
};

export const padCategories = (categories) => {
	const categoriesToDisplay = [...categories];
	const totalCategories = categoriesToDisplay.length;
	const remainder = totalCategories % MAX_CATEGORIES;

	if (remainder !== 0) {
		const emptyCategoriesCount = MAX_CATEGORIES - remainder;
		for (let i = 0; i < emptyCategoriesCount; i++) {
			categoriesToDisplay.push({category: "", idx: 10000 + i});
		}
	}

	return categoriesToDisplay;
};

export const sortCategories = (categories) => {
	return categories
		.filter(category => category !== null)
		.sort((a, b) => a.idx - b.idx);
};


export const generateUniqueLinkName = (
	links,
	name,
	category,
	isMergeCategory = false,
	isMigration = false,
	profileId = null
) => {
	const existingLinks = links.filter(link =>
		link.category === category
		&& link.name.replace(/ \(\d+\)$/, '') === name
		&& (!profileId || includesProfile(normalizeLink(link), profileId))
	);

	if (!isMergeCategory && isMigration) {
		return name;
	}
	return existingLinks.length > 0 ? `${name} (${existingLinks.length + 1})` : name;
};

export const MAX_CATEGORY_NAME_LENGTH = 16;
export const MAX_LINK_NAME_LENGTH = 18;
export const MAX_LINK_URL_LENGTH = 500;
export const MAX_PROFILE_NAME_LENGTH = 12;

export const validateFieldLength = (value, maxLength, fieldLabel, addError) => {
	const text = String(value ?? '').trim();
	if (text.length > maxLength) {
		addError(`${fieldLabel} must be ${maxLength} characters or fewer.`);
		return false;
	}
	return true;
};

const validateLinkFields = ({category, name, url, profileIds}, addError) => {
	if (!validateFieldLength(category, MAX_CATEGORY_NAME_LENGTH, 'Category', addError)) {
		return false;
	}
	if (!validateFieldLength(name, MAX_LINK_NAME_LENGTH, 'Link name', addError)) {
		return false;
	}
	if (!validateFieldLength(url, MAX_LINK_URL_LENGTH, 'URL', addError)) {
		return false;
	}
	if (!Array.isArray(profileIds) || profileIds.length === 0) {
		addError('At least one profile must be selected.');
		return false;
	}
	return true;
};

export const validateCategoryName = (name, addError = () => {}) =>
	validateFieldLength(name, MAX_CATEGORY_NAME_LENGTH, 'Category', addError);

export const validateProfileName = (name, addError = () => {}) =>
	validateFieldLength(name, MAX_PROFILE_NAME_LENGTH, 'Profile name', addError);

export const validateFormAddLink = (formData, addError = () => {}) => {
	const {category, name, url, image, profileIds} = formData;
	if (!category || !name || !url) {
		addError('All fields are required.');
		return false;
	}
	if (!validateLinkFields({category, name, url, profileIds}, addError)) {
		return false;
	}
	if (!isSafeLinkUrl(url)) {
		addError('URL must start with http:// or https://');
		return false;
	}
	if (image && typeof image !== 'string' && !SUPPORTED_LINK_IMAGE_FORMATS.includes(image.type)) {
		addError('Unsupported image format. Please upload a JPEG or PNG.');
		return false;
	}
	return true;
};

export const validateFormEditLink = (link, addError = () => {}) => {
	const {category, name, url, profileIds} = link;
	if (!category || !name || !url) {
		addError('All fields are required.');
		return false;
	}
	if (!validateLinkFields({category, name, url, profileIds}, addError)) {
		return false;
	}
	if (!isSafeLinkUrl(url)) {
		addError('URL must start with http:// or https://');
		return false;
	}
	return true;
};

export const getLinksByCategories = (categoriesToDisplay, links) => {
	return categoriesToDisplay.reduce((acc, categoryObj) => {
		const {category} = categoryObj;
		acc[category] = sortLinks(links.filter(link => link.category === category));
		return acc;
	}, {});
}

const resolveImageForUpload = async (image, linkName, addError, linkUrl) => {
	const faviconUrl = getFaviconImageUrl(image);
	if (faviconUrl) {
		const file = await fetchImageUrlAsFile(faviconUrl, {
			linkUrl,
			fetchStrategy: FAVICON_FETCH_STRATEGY.CALLABLE_ONLY,
		});
		if (file) {
			return uploadNewImage(file, linkName, addError);
		}
		return BUNDLED_DEFAULT_LINK_IMAGE;
	}

	if (typeof image === 'string') {
		if (isFirebaseStorageImageUrl(image)) {
			return image;
		}
		const file = await fetchImageUrlAsFile(image, {
			linkUrl,
			fetchStrategy: FAVICON_FETCH_STRATEGY.CALLABLE_ONLY,
		});
		if (file) {
			return uploadNewImage(file, linkName, addError);
		}
		return BUNDLED_DEFAULT_LINK_IMAGE;
	}

	if (!SUPPORTED_LINK_IMAGE_FORMATS.includes(image.type)) {
		alert('Unsupported image format. Please upload a JPEG or PNG.');
		return null;
	}

	return uploadNewImage(image, linkName, addError);
};

export const uploadImageToFirebase = async (image, linkName, addError, linkUrl) => {
	if (!image) {
		return BUNDLED_DEFAULT_LINK_IMAGE;
	}
	return resolveImageForUpload(image, linkName, addError, linkUrl);
};

export const getCategories = async (setCategories, addError, profile = ALL_PROFILE) => {
	const categories = await fetchCategories(addError);
	const normalized = (categories ?? []).map((category) =>
		normalizeCategory(category, profile?.id)
	);

	const filtered = isAllProfile(profile)
		? normalized
		: normalized.filter((category) => includesProfile(category, profile?.id));

	const withDisplayIdx = filtered.map((category) => ({
		...category,
		idx: getCategoryIdxForProfile(category, profile?.id ?? VIRTUAL_ALL_PROFILE_ID),
	}));

	setCategories(sortCategories(withDisplayIdx));
	return withDisplayIdx;
};

export const getLinks = async (setLinks, addError) => {
	const links = await fetchLinks(addError);
	const normalized = (links ?? []).map(normalizeLink);
	setLinks(normalized);
	return normalized;
}

export const filterLinksByProfile = (links, profile, profiles = []) => {
	if (!profile) return [];
	if (isAllProfile(profile)) {
		return (links ?? []).map(normalizeLink);
	}
	return links.filter((link) => includesProfile(normalizeLink(link), profile.id));
};

export const filterCategoriesWithLinksForProfile = (categories, profile, linksForProfile) => {
	if (!profile) return categories ?? [];
	const namesWithLinks = new Set(
		(linksForProfile ?? []).map((link) => link.category).filter(Boolean)
	);
	return (categories ?? []).filter((category) => namesWithLinks.has(category.category));
};

export const getProfiles = async (setProfiles, setProfile, addError) => {
	const result = await fetchProfiles(addError);
	const seenIds = new Set([VIRTUAL_ALL_PROFILE_ID]);
	const normalizedFetched = (result ?? [])
		.map(normalizeProfile)
		.filter((item) => item?.id && item.profileName !== "ALL")
		.filter((item) => {
			if (seenIds.has(item.id)) return false;
			seenIds.add(item.id);
			return true;
		});
	const normalized = [ALL_PROFILE, ...normalizedFetched];
	const sorted = sortProfiles(normalized);

	setProfiles(sorted);
	const savedProfileId = Cookies.get("profileId");
	const fallback = sorted.find((profile) => profile.id === savedProfileId)
		?? sorted[0]
		?? ALL_PROFILE;

	setProfile(fallback);
	Cookies.set("profileId", fallback.id, {expires: 7, path: "/"});
	return sorted;
};

export const handleImageUpload = async (image, linkName, addError, linkUrl) => {
	if (image) {
		return resolveImageForUpload(image, linkName, addError, linkUrl);
	}
	return BUNDLED_DEFAULT_LINK_IMAGE;
};

export const updateCategoryIndices = async (
	categories,
	categoryId,
	categoryIdx,
	oldIdx,
	addError,
	profileId,
	isRemoved = false
) => {
	try {
		if (!profileId) {
			addError('Missing profile ID while updating category indices');
			return categories;
		}

		const isAll = isAllProfile({id: profileId});
		const newIdx = Number(categoryIdx);
		const previousIdx = Number(oldIdx);
		let updatedCategories;

		if (isRemoved) {
			const removedCategory = categories.find(category => category.id === categoryId);
			if (!removedCategory) {
				addError(`Category with ID ${categoryId} not found.`);
				return categories;
			}

			if (isAll) {
				const removedIdx = toFiniteNumber(removedCategory.idx);
				updatedCategories = categories
					.filter(category => category.id !== categoryId)
					.map(category => ({
						...category,
						idx: toFiniteNumber(category.idx) > removedIdx
							? toFiniteNumber(category.idx) - 1
							: toFiniteNumber(category.idx)
					}));
			} else {
				updatedCategories = categories
					.filter(category => category.id !== categoryId)
					.map(category => ({
						...category,
						idxPerProfile: {
							...category.idxPerProfile,
							[profileId]: getCategoryIdxForProfile(category, profileId)
								> getCategoryIdxForProfile(removedCategory, profileId)
								? getCategoryIdxForProfile(category, profileId) - 1
								: getCategoryIdxForProfile(category, profileId)
						}
					}));
			}
		} else {
			updatedCategories = categories.map(category => {
				const currentIdx = isAll
					? toFiniteNumber(category.idx)
					: getCategoryIdxForProfile(category, profileId);
				if (category.id === categoryId) {
					if (isAll) {
						return {
							...category,
							idx: newIdx
						};
					}
					return {
						...category,
						idxPerProfile: {
							...category.idxPerProfile,
							[profileId]: newIdx
						}
					};
				}
				if (previousIdx > newIdx && currentIdx >= newIdx && currentIdx < previousIdx) {
					if (isAll) {
						return {...category, idx: currentIdx + 1};
					}
					return {
						...category,
						idxPerProfile: {
							...category.idxPerProfile,
							[profileId]: currentIdx + 1
						}
					};
				}
				if (previousIdx < newIdx && currentIdx > previousIdx && currentIdx <= newIdx) {
					if (isAll) {
						return {...category, idx: currentIdx - 1};
					}
					return {
						...category,
						idxPerProfile: {
							...category.idxPerProfile,
							[profileId]: currentIdx - 1
						}
					};
				}
				return category;
			});
		}

		await Promise.all(updatedCategories.map(category =>
			updateCategory(category, addError)
		));

		return updatedCategories;
	} catch (error) {
		addError(`Error updating categories: ${error.message}`);
		return categories;
	}
};

export const reindexCategoriesForProfiles = async (categories, profileIds, addError) => {
	try {
		const sourceCategories = categories ?? [];
		const normalizedProfileIds = Array.from(new Set(
			(profileIds ?? [])
				.map(normalizeProfileId)
				.filter((profileId) => profileId && profileId !== VIRTUAL_ALL_PROFILE_ID)
		));

		if (normalizedProfileIds.length === 0) {
			return sourceCategories;
		}

		const nextCategories = sourceCategories.map((category) => ({
			...category,
			idxPerProfile: {...category.idxPerProfile}
		}));

		normalizedProfileIds.forEach((profileId) => {
			const sortedForProfile = nextCategories
				.filter((category) => includesProfile(category, profileId))
				.sort((a, b) => getCategoryIdxForProfile(a, profileId) - getCategoryIdxForProfile(b, profileId));

			sortedForProfile.forEach((category, idx) => {
				category.idxPerProfile[profileId] = idx;
			});
		});

		nextCategories.forEach((category) => {
			const idxValues = Object.values(category.idxPerProfile ?? {})
				.map(Number)
				.filter(Number.isFinite);
			if (idxValues.length > 0) {
				category.idx = Math.min(...idxValues);
			}
		});

		const writes = nextCategories.filter((category, index) => {
			const previous = sourceCategories[index];
			if (!previous || previous.id !== category.id) return true;
			if (Number(previous.idx) !== Number(category.idx)) return true;

			const prevEntries = Object.entries(previous.idxPerProfile ?? {});
			const nextEntries = Object.entries(category.idxPerProfile ?? {});
			if (prevEntries.length !== nextEntries.length) return true;

			return nextEntries.some(([profileId, idx]) => Number(previous.idxPerProfile?.[profileId]) !== Number(idx));
		});

		await Promise.all(writes.map((category) => updateCategory(category, addError)));
		return nextCategories;
	} catch (error) {
		addError(`Error while reindexing categories by profile: ${error.message}`);
		return categories;
	}
};

export const mergeUniqueImageUrls = (...urlGroups) => {
	const seen = new Map();
	urlGroups.flat().forEach((url) => {
		if (typeof url !== 'string' || !url) return;
		const baseUrl = url.split('?')[0];
		if (!seen.has(baseUrl)) {
			seen.set(baseUrl, url);
		}
	});
	return Array.from(seen.values());
};

export const getUniqueImagesFromLinks = (links) => mergeUniqueImageUrls(
	(links ?? []).map((link) => link.image).filter(isFirebaseStorageImageUrl)
);

export const verifyParamsUpdateLink = async (link, addError) => {
	if (!link?.id) {
		addError("Invalid link ID for update");
		return null;
	}

	const downloadUrl = await uploadImageToFirebase(link.image, link.name, addError, link.url);

	if (!downloadUrl || downloadUrl.empty) {
		addError("Download URL is empty");
		return null;
	}

	return downloadUrl;
};

export const ensureCategoryHasProfiles = async (
	category,
	profileIds,
	allCategories,
	setAllCategories,
	addError
) => {
	const existingCategory = allCategories.find((item) => item.id === category.id);
	if (!existingCategory) return category;

	const incomingProfileIds = normalizeProfileIds(profileIds ?? []);
	const existingHasAllScope = hasAllProfilesScope(existingCategory);
	const incomingHasAllScope = incomingProfileIds.includes(VIRTUAL_ALL_PROFILE_ID);

	if (existingHasAllScope && incomingHasAllScope) {
		return existingCategory;
	}

	const mergedProfileIds = incomingHasAllScope
		? [VIRTUAL_ALL_PROFILE_ID]
		: Array.from(new Set([
			...(existingCategory.profileIds ?? []).filter((id) => id !== VIRTUAL_ALL_PROFILE_ID),
			...incomingProfileIds.filter((id) => id !== VIRTUAL_ALL_PROFILE_ID)
		]));

	const updated = {
		...existingCategory,
		profileIds: mergedProfileIds,
		idxPerProfile: {...existingCategory.idxPerProfile}
	};

	let changed = mergedProfileIds.length !== (existingCategory.profileIds ?? []).length
		|| incomingHasAllScope !== existingHasAllScope;

	if (!incomingHasAllScope) {
		incomingProfileIds
			.filter((profileId) => profileId !== VIRTUAL_ALL_PROFILE_ID)
			.forEach((profileId) => {
				if (updated.idxPerProfile[profileId] == null) {
					updated.idxPerProfile[profileId] = calculateNextIdxForProfile(profileId, allCategories);
					changed = true;
				}
			});
	}

	if (changed) {
		await updateCategory(updated, addError);
		setAllCategories((prev) => prev.map((item) => item.id === updated.id ? updated : item));
	}

	return updated;
};

export const handleAddCategory = async ({
	allCategories,
	setAllCategories,
	setCategories,
	category,
	profileIds,
	addError,
	categories = []
}) => {
	const incomingProfileIds = normalizeProfileIds(profileIds ?? []);
	const hasAllScope = incomingProfileIds.includes(VIRTUAL_ALL_PROFILE_ID);
	const normalizedProfileIds = hasAllScope
		? [VIRTUAL_ALL_PROFILE_ID]
		: incomingProfileIds.filter((profileId) => profileId !== VIRTUAL_ALL_PROFILE_ID);

	const normalizedCategoryName = formatCategoryName(category);
	const existing = allCategories.find((item) =>
		normalizeCategoryNameForComparison(item.category) === normalizeCategoryNameForComparison(normalizedCategoryName)
	);
	if (existing) {
		const ensured = await ensureCategoryHasProfiles(
			existing,
			normalizedProfileIds,
			allCategories,
			setAllCategories,
			addError
		);
		if (setCategories) {
			setCategories((prev) => {
				const has = prev.some((item) => item.id === ensured.id);
				return has ? prev : [...prev, ensured];
			});
		}
		return ensured.id;
	}

	const idxPerProfile = {};
	if (!hasAllScope) {
		normalizedProfileIds.forEach((profileId) => {
			idxPerProfile[profileId] = toFiniteNumber(calculateNextIdxForProfile(profileId, categories));
		});
	}

	const newCategory = normalizeCategory({
		category: normalizedCategoryName,
		profileIds: normalizedProfileIds,
		idxPerProfile,
		idx: hasAllScope
			? toFiniteNumber(categories.length)
			: normalizedProfileIds.length
				? toFiniteNumber(Math.min(...Object.values(idxPerProfile)))
				: 0
	});

	const response = await addCategory(newCategory, addError);
	if (!response?.id) {
		return null;
	}

	const newCategoryWithId = {...newCategory, id: response.id};
	setAllCategories((prevCategories) => {
		if (prevCategories.some((item) => item.id === newCategoryWithId.id)) {
			return prevCategories;
		}
		return [...prevCategories, newCategoryWithId];
	});
	if (setCategories) {
		setCategories((prevCategories) => {
			if (prevCategories.some((item) => item.id === newCategoryWithId.id)) {
				return prevCategories;
			}
			return [...prevCategories, newCategoryWithId];
		});
	}
	return response.id;
};
